import type { MainGoal, TrainingLevel, TrainingLocation } from '../types/registration';
import type { TrainingCategory } from '../types/training';

export type WorkoutImportStatus = 'ready' | 'duplicate' | 'blocked';

export interface WorkoutImportRow {
  id: string;
  name: string;
  status: WorkoutImportStatus;
  reason: string | null;
  levels: TrainingLevel[];
  objective: MainGoal[];
  category: TrainingCategory | null;
  monthIndex: number | null;
  sessionsPerWeek: number | null;
  durationMinutes: number | null;
  location: TrainingLocation | null;
}

const WORKOUT_HISTORY_DOMAIN = 'WorkoutHistory';
const CODE_PATTERN = /^(?:IA-MNP|AVAN|INT|INI|MNP)(?:-[A-Za-z0-9]+)+$/;
const OBJECTIVE_PATTERN = /\b(HPT|EMAG)\b/gi;
const SUFFIX_PATTERN = /^(?:\s*(?:-\s*|\(\s*)?(?:PP|GEST|MNP|HPT|EMAG)\b\s*\)?)+/i;
const BLOCK_SEGMENTS = new Set(['ABC', 'ABCD', 'ABCDE']);

const LEVEL_BY_PREFIX = {
  INI: 'beginner',
  INT: 'intermediate',
  AVAN: 'advanced',
} as const satisfies Record<string, TrainingLevel>;

const CATEGORY_BY_TAG = {
  PP: 'postpartum',
  GEST: 'pregnant',
  MNP: 'perimenopause_menopause',
} as const satisfies Record<string, TrainingCategory>;

const REASON_OUT_OF_PATTERN = 'Texto fora do padrão do código';
const REASON_NO_LEVEL = 'Sem nível reconhecido';
const REASON_INCOMPLETE = 'Código incompleto';
const REASON_FREQUENCY = 'Frequência inválida';
const REASON_MONTH = 'Mês inválido';
const REASON_CATEGORY = 'Categorias conflitantes';

interface PrescriptionDraft {
  name: string;
  level: TrainingLevel | null;
  location: TrainingLocation | null;
  sessionsPerWeek: number | null;
  durationMinutes: number | null;
  monthIndex: number | null;
  categories: Set<'PP' | 'GEST' | 'MNP'>;
  objectives: Set<'HPT' | 'EMAG'>;
  incomplete: boolean;
  frequencyInvalid: boolean;
  monthInvalid: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const readText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const stripTrailingNovo = (code: string): string => {
  const trimmed = code.trim();
  const stripped = trimmed.replace(/-?novo$/i, '');
  return stripped.length > 0 ? stripped : trimmed;
};

const addObjectiveTags = (text: string, objectives: Set<'HPT' | 'EMAG'>) => {
  const matches = text.toUpperCase().match(OBJECTIVE_PATTERN) ?? [];
  matches.forEach((tag) => {
    if (tag === 'HPT' || tag === 'EMAG') {
      objectives.add(tag);
    }
  });
};

const addSuffixTags = (
  code: string,
  rawValue: string,
  categories: Set<'PP' | 'GEST' | 'MNP'>,
  objectives: Set<'HPT' | 'EMAG'>,
) => {
  const upperCode = code.toUpperCase();
  const upperRaw = rawValue.toUpperCase();
  const at = upperRaw.indexOf(upperCode);
  if (at < 0) {
    return;
  }

  const suffix = rawValue.slice(at + code.length).match(SUFFIX_PATTERN)?.[0] ?? '';
  const tags = suffix.toUpperCase().match(/\b(PP|GEST|MNP|HPT|EMAG)\b/g) ?? [];
  tags.forEach((tag) => {
    if (tag === 'HPT' || tag === 'EMAG') {
      objectives.add(tag);
      return;
    }
    if (tag === 'PP' || tag === 'GEST' || tag === 'MNP') {
      categories.add(tag);
    }
  });
};

const addNoteTags = (
  code: string,
  rawValue: string,
  categories: Set<'PP' | 'GEST' | 'MNP'>,
  objectives: Set<'HPT' | 'EMAG'>,
) => {
  addSuffixTags(code, rawValue, categories, objectives);
  addObjectiveTags(rawValue, objectives);
};

const takePrefix = (code: string, prefix: string): string => (
  code.slice(prefix.length).replace(/^-/, '')
);

const parsePrescription = (code: string, rawValue: string): PrescriptionDraft => {
  const name = stripTrailingNovo(code).toUpperCase();
  const draft: PrescriptionDraft = {
    name,
    level: null,
    location: null,
    sessionsPerWeek: null,
    durationMinutes: null,
    monthIndex: null,
    categories: new Set(),
    objectives: new Set(),
    incomplete: !CODE_PATTERN.test(name),
    frequencyInvalid: false,
    monthInvalid: false,
  };

  let rest = name;
  if (!draft.incomplete) {
    if (rest.startsWith('IA-MNP')) {
      rest = takePrefix(rest, 'IA-MNP');
    } else if (rest.startsWith('AVAN') || rest.startsWith('INT') || rest.startsWith('INI')) {
      const prefix = rest.startsWith('AVAN') ? 'AVAN' : rest.slice(0, 3);
      draft.level = LEVEL_BY_PREFIX[prefix as keyof typeof LEVEL_BY_PREFIX];
      rest = takePrefix(rest, prefix);
    } else if (rest.startsWith('MNP')) {
      draft.categories.add('MNP');
      rest = takePrefix(rest, 'MNP');
    }
  }

  const applyMonth = (month: number) => {
    if (draft.monthIndex !== null) {
      draft.incomplete = true;
    }
    if (month < 1 || month > 24) {
      draft.monthInvalid = true;
    }
    draft.monthIndex = month;
  };

  const segments = rest.length > 0 ? rest.split('-') : [];
  segments.forEach((segment) => {
    if (draft.incomplete && !CODE_PATTERN.test(name)) {
      return;
    }
    if (segment === 'PP' || segment === 'GEST' || segment === 'MNP') {
      draft.categories.add(segment);
      return;
    }
    if (segment === 'HPT' || segment === 'EMAG') {
      draft.objectives.add(segment);
      return;
    }
    if (BLOCK_SEGMENTS.has(segment)) {
      return;
    }
    if (segment === 'A' || segment === 'C') {
      if (draft.location) {
        draft.incomplete = true;
      }
      draft.location = segment === 'A' ? 'full_gym' : 'home_condo_gym';
      return;
    }

    const frequencyMatch = /^(\d+)X$/.exec(segment);
    if (frequencyMatch) {
      const frequency = Number(frequencyMatch[1]);
      if (draft.sessionsPerWeek !== null) {
        draft.incomplete = true;
      }
      if (frequency < 1) {
        draft.frequencyInvalid = true;
      }
      draft.sessionsPerWeek = frequency;
      return;
    }

    const monthMatch = /^M(\d+)$/.exec(segment);
    if (monthMatch) {
      applyMonth(Number(monthMatch[1]));
      return;
    }

    const gluedMonthMatch = /^[A-Z]+M(\d+)$/.exec(segment);
    if (gluedMonthMatch) {
      applyMonth(Number(gluedMonthMatch[1]));
      return;
    }

    if (/^\d{2,3}$/.test(segment)) {
      const duration = Number(segment);
      if (draft.durationMinutes !== null || duration <= 0) {
        draft.incomplete = true;
      }
      draft.durationMinutes = duration;
    }
  });

  addNoteTags(name, rawValue, draft.categories, draft.objectives);
  return draft;
};

const resolveCategory = (categories: Set<'PP' | 'GEST' | 'MNP'>): TrainingCategory | null => {
  if (categories.size > 1) {
    return null;
  }
  if (categories.size === 0) {
    return 'performance';
  }
  const tag = categories.values().next().value;
  if (tag === undefined) {
    return 'performance';
  }
  return CATEGORY_BY_TAG[tag];
};

const resolveObjectives = (objectives: Set<'HPT' | 'EMAG'>): MainGoal[] => {
  const values: MainGoal[] = [];
  if (objectives.has('HPT')) {
    values.push('hypertrophy');
  }
  if (objectives.has('EMAG')) {
    values.push('weight_loss');
  }
  return values;
};

const resolveReason = (draft: PrescriptionDraft, category: TrainingCategory | null): string | null => {
  if (!CODE_PATTERN.test(draft.name)) {
    return REASON_INCOMPLETE;
  }
  if (draft.level === null) {
    return REASON_NO_LEVEL;
  }
  if (draft.incomplete) {
    return REASON_INCOMPLETE;
  }
  if (draft.frequencyInvalid) {
    return REASON_FREQUENCY;
  }
  if (draft.monthInvalid) {
    return REASON_MONTH;
  }
  if (category === null) {
    return REASON_CATEGORY;
  }
  return null;
};

const toPrescriptionRow = (draft: PrescriptionDraft): WorkoutImportRow => {
  const category = resolveCategory(draft.categories);
  const objective = resolveObjectives(draft.objectives);
  const reason = resolveReason(draft, category);

  return {
    id: draft.name,
    name: draft.name,
    status: reason === null ? 'ready' : 'blocked',
    reason,
    levels: draft.level === null ? [] : [draft.level],
    objective,
    category,
    monthIndex: draft.monthIndex,
    sessionsPerWeek: draft.sessionsPerWeek,
    durationMinutes: draft.durationMinutes,
    location: draft.location,
  };
};

const blockedTextRow = (rawValue: string): WorkoutImportRow => ({
  id: `raw:${rawValue.trim().toUpperCase()}`,
  name: rawValue.trim(),
  status: 'blocked',
  reason: REASON_OUT_OF_PATTERN,
  levels: [],
  objective: [],
  category: null,
  monthIndex: null,
  sessionsPerWeek: null,
  durationMinutes: null,
  location: null,
});

const statusOrder: Record<WorkoutImportStatus, number> = {
  ready: 0,
  duplicate: 1,
  blocked: 2,
};

export const sortWorkoutImportRows = (rows: readonly WorkoutImportRow[]): WorkoutImportRow[] => (
  [...rows].sort((left, right) => (
    statusOrder[left.status] - statusOrder[right.status]
    || left.name.localeCompare(right.name, 'pt-BR')
  ))
);

export const markExistingWorkoutImports = (
  rows: readonly WorkoutImportRow[],
  existingNames: readonly string[],
): WorkoutImportRow[] => {
  const existing = new Set(existingNames.map((name) => name.trim().toLowerCase()).filter((name) => name.length > 0));

  return sortWorkoutImportRows(rows.map((row) => {
    if (row.status === 'blocked' || !existing.has(row.name.trim().toLowerCase())) {
      return row;
    }

    return {
      ...row,
      status: 'duplicate',
      reason: 'Já cadastrado',
    };
  }));
};

export const parseWorkoutHistoryImport = (payload: unknown): WorkoutImportRow[] => {
  if (!isRecord(payload) || !isRecord(payload.metadata) || payload.metadata.domain !== WORKOUT_HISTORY_DOMAIN) {
    throw new Error('Envie um JSON de histórico de treinos (workout-history).');
  }

  if (!Array.isArray(payload.students)) {
    throw new Error('Envie um JSON de histórico de treinos (workout-history).');
  }

  const prescriptions = new Map<string, PrescriptionDraft>();
  const texts: WorkoutImportRow[] = [];
  const textKeys = new Set<string>();

  payload.students.forEach((student) => {
    if (!isRecord(student) || !Array.isArray(student.records)) {
      return;
    }

    student.records.forEach((record) => {
      if (!isRecord(record)) {
        return;
      }

      const rawValue = readText(record.rawValue);
      const parsedCode = stripTrailingNovo(readText(record.parsedCode));
      const isPrescription = record.entryKind === 'Prescription' && parsedCode.length > 0;

      if (isPrescription) {
        const key = parsedCode.toUpperCase();
        const current = prescriptions.get(key);
        if (!current) {
          prescriptions.set(key, parsePrescription(parsedCode, rawValue));
          return;
        }
        addNoteTags(parsedCode, rawValue, current.categories, current.objectives);
        return;
      }

      if (rawValue.length === 0) {
        return;
      }

      const textKey = rawValue.toUpperCase();
      if (textKeys.has(textKey) || prescriptions.has(textKey)) {
        return;
      }
      textKeys.add(textKey);
      texts.push(blockedTextRow(rawValue));
    });
  });

  const prescriptionKeys = new Set(prescriptions.keys());

  return sortWorkoutImportRows([
    ...Array.from(prescriptions.values(), toPrescriptionRow),
    ...texts.filter((row) => !prescriptionKeys.has(row.name.trim().toUpperCase())),
  ]);
};
